import React, { useState, useEffect, useMemo } from 'react';
import { useBooking } from '../context/BookingContext';
import { apiService } from '../services/apiService';

const Member = ({ navigateTo }) => {
  const { state, dispatch } = useBooking();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('upcoming');
  const [user, setUser] = useState(null);
  const [bookings, setBookings] = useState({ upcoming: [], previous: [] });
  const [loading, setLoading] = useState(true);
  const [profileLoading, setProfileLoading] = useState(false);
  const [formData, setFormData] = useState({});
  const [cancellingId, setCancellingId] = useState(null);
  const [titles, setTitles] = useState([]);
  const [titlesLoading, setTitlesLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Check if user is logged in
  useEffect(() => {
    const userData = sessionStorage.getItem('userData');
    const userToken = sessionStorage.getItem('userToken');

    if (!userData || !userToken) {
      // Redirect to home if not logged in
      navigateTo('index');
      return;
    }

    try {
      const parsedUser = JSON.parse(userData);
      console.log('User data from session:', parsedUser);
      
      // The user ID is id_mst_guest_detail from the response
      setUser(parsedUser);
      fetchUserData(parsedUser.id_mst_guest_detail);
    } catch (error) {
      console.error('Error parsing user data:', error);
      navigateTo('home');
    }
  }, [navigateTo]);

  // Filter bookings based on search term
  const filteredBookings = useMemo(() => {
    if (!searchTerm.trim()) {
      return bookings;
    }

    const searchLower = searchTerm.toLowerCase();
    
    const filterBookingsList = (bookingsList) => {
      return bookingsList.filter(booking => {
        return (
          (booking.hotelName?.toLowerCase().includes(searchLower)) ||
          (booking.guestName?.toLowerCase().includes(searchLower)) ||
          (booking.status?.toLowerCase().includes(searchLower)) ||
          (booking.email?.toLowerCase().includes(searchLower)) ||
          (booking.trackingId?.toLowerCase().includes(searchLower)) ||
          (booking.bookingDate?.toLowerCase().includes(searchLower))
        );
      });
    };

    return {
      upcoming: filterBookingsList(bookings.upcoming),
      previous: filterBookingsList(bookings.previous)
    };
  }, [bookings, searchTerm]);

  // Fetch titles when drawer opens
  useEffect(() => {
    if (isDrawerOpen) {
      fetchTitles();
    }
  }, [isDrawerOpen]);

  const fetchTitles = async () => {
    setTitlesLoading(true);
    try {
      const response = await apiService.getTitles();
      console.log('Titles response:', response);
      
      if (response.status && response.data) {
        // Transform the data if needed - assuming response.data is an array of titles
        // Adjust this based on your actual API response structure
        setTitles(response.data);
      } else {
        console.error('Failed to fetch titles:', response.message);
      }
    } catch (error) {
      console.error('Error fetching titles:', error);
    } finally {
      setTitlesLoading(false);
    }
  };

  const fetchUserData = async (userId) => {
    setLoading(true);
    try {
      console.log('Fetching data for user ID:', userId);
      
      // Fetch profile and bookings in parallel using the guest detail ID
      const [profileResponse, bookingsResponse] = await Promise.all([
        apiService.getUserProfile(userId),
        apiService.getUserBookings(userId)
      ]);

      if (profileResponse.success && profileResponse.data) {
        const profileData = profileResponse.data;
        
        // Update user state with profile data
        setUser(currentUser => ({
          ...currentUser,
          user: {
            ...currentUser.user,
            ...profileData,
            // Keep the original id_mst_guest_detail
            id_mst_guest_detail: userId
          }
        }));
        
        // Set form data for editing
        setFormData({
          title: profileData.title || '',
          title_id: profileData.title_id || '',
          firstName: profileData.firstName || profileData.name?.split(' ')[0] || '',
          lastName: profileData.lastName || profileData.name?.split(' ').slice(1).join(' ') || '',
          email: profileData.email || '',
          phone: profileData.phone || '',
          companyName: profileData.companyName || '',
          designation: profileData.designation || '',
          city: profileData.city || '',
          pincode: profileData.pincode || '',
          dateOfBirth: profileData.dateOfBirth || '',
          address: profileData.address || '',
          memberSince: profileData.memberSince || 'Feb 2026',
          id: profileData.id || userId
        });
      } else {
        console.error('Profile fetch failed:', profileResponse?.message);
        // Still set form data with existing user data
        setFormData({
          title: '',
          title_id: '',
          firstName: user?.user?.name?.split(' ')[0] || '',
          lastName: user?.user?.name?.split(' ').slice(1).join(' ') || '',
          email: user?.user?.email || '',
          phone: user?.user?.phone || '',
          companyName: '',
          designation: '',
          city: '',
          pincode: '',
          dateOfBirth: '',
          address: '',
          memberSince: 'Feb 2026',
          id: userId
        });
      }

      if (bookingsResponse.success) {
        setBookings(bookingsResponse.data);
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEditProfile = () => {
    setIsDrawerOpen(true);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleSaveProfile = async () => {
    if (!user?.user?.id_mst_guest_detail) return;

    setProfileLoading(true);
    try {
      const userId = user.user.id_mst_guest_detail;
      console.log('Updating profile for user ID:', userId, 'with data:', formData);
      
      const response = await apiService.updateUserProfile(userId, formData);
      
      if (response.success) {
        // Find the selected title text
        const selectedTitle = titles.find(t => t.id == formData.title_id) || { name: formData.title };
        
        // Update user state with the response data from API
        setUser(prev => ({
          ...prev,
          user: {
            ...prev.user,
            ...response.data, // Use the data returned from API
            name: response.data.name || `${formData.firstName} ${formData.lastName}`.trim(),
            phone: response.data.phone || formData.phone,
            title: selectedTitle.name || formData.title,
            title_id: formData.title_id,
            city: response.data.city || formData.city,
            pincode: response.data.pincode || formData.pincode,
            address: response.data.address || formData.address,
            companyName: response.data.company_name || formData.companyName, // Update company name
            designation: response.data.designation || formData.designation,
            dateOfBirth: response.data.dateOfBirth || formData.dateOfBirth,
          }
        }));
        
        // Update session storage with new data
        const sessionData = JSON.parse(sessionStorage.getItem('userData') || '{}');
        sessionData.user = {
          ...sessionData.user,
          name: response.data.name || `${formData.firstName} ${formData.lastName}`.trim(),
          phone: response.data.phone || formData.phone
        };
        sessionStorage.setItem('userData', JSON.stringify(sessionData));
        
        setIsDrawerOpen(false);
        alert('Profile updated successfully!');
      } else {
        alert(response.message || 'Failed to update profile');
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      alert('Failed to update profile');
    } finally {
      setProfileLoading(false);
    }
  };

  // const handleCancelBooking = async (bookingId) => {
  //   if (!window.confirm('Are you sure you want to cancel this booking?')) {
  //     return;
  //   }

  //   setCancellingId(bookingId);
  //   try {
  //     const response = await apiService.cancelBooking(bookingId);
      
  //     if (response.success) {
  //       // Update bookings list
  //       setBookings(prev => ({
  //         ...prev,
  //         upcoming: prev.upcoming.filter(b => b.id !== bookingId)
  //       }));
  //       alert('Booking cancelled successfully');
  //     } else {
  //       alert(response.message || 'Failed to cancel booking');
  //     }
  //   } catch (error) {
  //     console.error('Error cancelling booking:', error);
  //     alert('Failed to cancel booking');
  //   } finally {
  //     setCancellingId(null);
  //   }
  // };

  const handleCancelBooking = async (bookingId) => {
  if (!window.confirm('Are you sure you want to cancel this booking?')) {
    return;
  }

  setCancellingId(bookingId);
  try {
    const response = await apiService.cancelBooking(bookingId);
    
    if (response.success) {
      // Refresh all bookings from the server
      if (user?.user?.id_mst_guest_detail) {
        const bookingsResponse = await apiService.getUserBookings(user.user.id_mst_guest_detail);
        if (bookingsResponse.success) {
          setBookings(bookingsResponse.data);
        }
      }
      alert('Booking cancelled successfully');
    } else {
      alert(response.message || 'Failed to cancel booking');
    }
  } catch (error) {
    console.error('Error cancelling booking:', error);
    alert('Failed to cancel booking');
  } finally {
    setCancellingId(null);
  }
};

  const handleLogout = () => {
    // Clear session storage
    sessionStorage.removeItem('userData');
    sessionStorage.removeItem('userToken');
    sessionStorage.removeItem('refresh_token');
    
    // Clear user from context
    dispatch({ type: 'CLEAR_USER' });
    
    // Navigate to home
    navigateTo('index');
  };

  useEffect(() => {
    if (isDrawerOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isDrawerOpen]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-800 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading your profile...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return null; // Will redirect in useEffect
  }

  // Get user display name and details
  const displayName = user.user?.name || 'User';
  const userEmail = user.user?.email || '';
  const userPhone = formData.phone || user.user?.phone || 'Not provided';
  const userAddress = formData.address || 'Not provided';
  const memberId = `WH-${user.user?.id_mst_guest_detail || '000'}`;
  const memberSince = formData.memberSince || 'Feb 2026';
  
  // Generate avatar URL with name
  const avatarUrl = `https://ui-avatars.com/api/?name=${encodeURIComponent(displayName)}&background=111827&color=fff`;

  return (
    <div className="min-h-screen bg-gray-50 font-sans text-slate-900 relative">
      
      <main className="w-full max-w-[1600px] mx-auto p-4 lg:p-8 pt-10">
        <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
          
          {/* Sidebar */}
          <div className="w-full lg:w-[300px] flex-shrink-0">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              
              {/* Top Section */}
              <div className="px-8 py-4 flex flex-col items-center border-b border-gray-100">
                <div className="relative mb-2 group">
                  <img 
                    src={avatarUrl}
                    alt="Profile" 
                    className="w-24 h-24 rounded-full object-cover border-4 border-gray-50 shadow-inner" 
                  />
                  <button 
                    onClick={handleEditProfile}
                    className="absolute bottom-0 right-0 bg-blue-600 text-white p-2 rounded-full shadow-lg hover:bg-blue-700 transition-transform hover:scale-110"
                    title="Edit Profile"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                      <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                    </svg>
                  </button>
                </div>
                <h2 className="text-xl font-bold mb-3">{displayName}</h2>
                <span className="inline-flex items-center gap-1.5 px-4 py-1.5 bg-emerald-50 text-emerald-700 text-xs font-bold rounded-full border border-emerald-200">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7" />
                  </svg>
                  Verified Profile
                </span>
              </div>

              {/* Bottom Section */}
              <div className="px-6 py-4 space-y-5 bg-gray-50/50">
                <div className="space-y-4">
                  {/* Email */}
                  <div className="flex items-center gap-3 text-sm text-gray-600">
                    <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    <span className="truncate" title={userEmail}>{userEmail}</span>
                  </div>
                  
                  {/* Phone */}
                  <div className="flex items-center gap-3 text-sm text-gray-600">
                    <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                    <span>{userPhone}</span>
                  </div>

                  {/* Address */}
                  <div className="flex items-start gap-3 text-sm text-gray-600">
                    <svg className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.243-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    <span className="leading-snug">{userAddress}</span>
                  </div>
                </div>

                <hr className="border-gray-200" />

                {/* Account Meta */}
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-xs font-bold text-gray-400 uppercase">Member ID</span>
                    <span className="text-sm font-mono font-bold text-gray-700">{memberId}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-xs font-bold text-gray-400 uppercase">Joined</span>
                    <span className="text-sm font-semibold text-gray-700">{memberSince}</span>
                  </div>
                </div>
                
                <button 
                  onClick={handleLogout}
                  className="w-full mt-2 py-2.5 bg-white border border-red-200 text-red-600 text-sm font-bold rounded-lg hover:bg-red-50 transition-colors shadow-sm"
                >
                  Log Out
                </button>
              </div>
              
            </div>
          </div>

          {/* Bookings Table */}
          <div className="flex-1 min-w-0 bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            
            <div className="border-b border-gray-200 flex flex-wrap items-end justify-between px-6 gap-4 align-middle py-2">
              <div className="flex space-x-8">
                <button 
                  onClick={() => setActiveTab('upcoming')}
                  className={`pb-4 text-xs font-bold uppercase tracking-wide transition-colors relative ${activeTab === 'upcoming' ? 'text-red-800' : 'text-gray-500 hover:text-gray-800'}`}
                >
                  Upcoming Bookings ({filteredBookings.upcoming.length})
                  {activeTab === 'upcoming' && <div className="absolute bottom-0 left-0 w-full h-1 bg-red-800 rounded-t-md"></div>}
                </button>
                <button 
                  onClick={() => setActiveTab('previous')}
                  className={`pb-4 text-xs font-bold uppercase tracking-wide transition-colors relative ${activeTab === 'previous' ? 'text-red-800' : 'text-gray-500 hover:text-gray-800'}`}
                >
                  Previous Bookings ({filteredBookings.previous.length})
                  {activeTab === 'previous' && <div className="absolute bottom-0 left-0 w-full h-1 bg-red-800 rounded-t-md"></div>}
                </button>

                <button 
                  onClick={() => navigateTo('index')}
                  className={`pb-4 text-xs font-bold uppercase tracking-wide transition-colors relative`}
                >
                  Book Now
                </button>
              </div>
              
              <div className="relative pb-0">
                <input 
                  type="text" 
                  placeholder="Search by hotel, guest, status, email..." 
                  value={searchTerm}
                  onChange={handleSearchChange}
                  className="pl-4 pr-10 py-2 border border-gray-300 rounded-md text-sm focus:ring-1 focus:ring-red-800 focus:border-red-800 outline-none w-64 transition-all" 
                />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm('')}
                    className="absolute inset-y-0 right-6 pr-3 pb-0 flex items-center text-gray-400 hover:text-gray-600"
                  >
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
                <span className="absolute inset-y-0 right-0 pr-3 pb-0 flex items-center pointer-events-none">
                  <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
                  </svg>
                </span>
              </div>
            </div>

            <div className="overflow-x-auto min-h-[400px]">
              <table className="w-full text-left whitespace-nowrap">
                <thead className="bg-gray-100 text-gray-600 text-xs font-bold border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-4">S.no</th>
                    <th className="px-4 py-4">Booking Date</th>
                    <th className="px-4 py-4">Hotel Name</th>
                    <th className="px-4 py-4">Guest Name</th>
                    <th className="px-4 py-4">Email</th>
                    <th className="px-4 py-4">Check-in Date</th>
                    <th className="px-4 py-4">Check-out Date</th>
                    <th className="px-4 py-4">Tracking Id</th>
                    <th className="px-4 py-4">Booking Status</th>
                    {activeTab === 'upcoming' && (
                      <th className="px-4 py-4 text-center">Action</th>
                    )}
                  </tr>
                </thead>
                
                <tbody className="divide-y divide-gray-100 text-sm">
                  {activeTab === 'upcoming' ? (
                    filteredBookings.upcoming.length > 0 ? (
                      filteredBookings.upcoming.map((booking, index) => (
                        <tr key={booking.id} className="hover:bg-gray-50 transition-colors">
                          <td className="px-4 py-4 font-medium text-gray-500">{index + 1}</td>
                          <td className="px-4 py-4">{booking.bookingDate}</td>
                          <td className="px-4 py-4 font-bold text-gray-900">{booking.hotelName}</td>
                          <td className="px-4 py-4">{booking.guestName || displayName}</td>
                          <td className="px-4 py-4">{booking.email || userEmail}</td>
                          <td className="px-4 py-4 font-semibold text-gray-700">{booking.checkInDate}</td>
                          <td className="px-4 py-4">{booking.checkOutDate}</td>
                          <td className="px-4 py-4 font-mono text-gray-500">{booking.trackingId}</td>
                          <td className="px-4 py-4">
                            <span className={`font-bold ${
                              booking.status === 'Confirmed' ? 'text-green-600' :
                              booking.status === 'Pending' ? 'text-yellow-600' :
                              booking.status === 'Completed' ? 'text-blue-600' :
                              booking.status === 'Cancelled' ? 'text-red-600' :
                              'text-gray-600'
                            }`}>
                              {booking.status}
                            </span>
                          </td>
                          <td className="px-4 py-4 text-center">
                            {booking.status === 'Cancelled' || booking.status === 'Failed' || booking.status === 'Completed' ? (
                              <span className="text-gray-400 text-xs font-medium">-</span>
                            ) : (
                              <button 
                                onClick={() => handleCancelBooking(booking.id)}
                                disabled={cancellingId === booking.id}
                                className={`px-4 py-1.5 bg-white text-red-600 hover:bg-red-600 hover:text-white border border-red-200 rounded text-xs font-bold transition-colors shadow-sm ${
                                  cancellingId === booking.id ? 'opacity-50 cursor-not-allowed' : ''
                                }`}
                              >
                                {cancellingId === booking.id ? 'Cancelling...' : 'Cancel'}
                              </button>
                            )}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="10" className="px-4 py-24 text-center text-gray-500 font-medium bg-white">
                          {searchTerm ? 'No upcoming bookings match your search' : 'No upcoming bookings found'}
                        </td>
                      </tr>
                    )
                  ) : (
                    filteredBookings.previous.length > 0 ? (
                      filteredBookings.previous.map((booking, index) => (
                        <tr key={booking.id} className="hover:bg-gray-50 transition-colors">
                          <td className="px-4 py-4 font-medium text-gray-500">{index + 1}</td>
                          <td className="px-4 py-4">{booking.bookingDate}</td>
                          <td className="px-4 py-4 font-bold text-gray-900">{booking.hotelName}</td>
                          <td className="px-4 py-4">{booking.guestName || displayName}</td>
                          <td className="px-4 py-4">{booking.email || userEmail}</td>
                          <td className="px-4 py-4 font-semibold text-gray-700">{booking.checkInDate}</td>
                          <td className="px-4 py-4">{booking.checkOutDate}</td>
                          <td className="px-4 py-4 font-mono text-gray-500">{booking.trackingId}</td>
                          <td className="px-4 py-4">
                            <span className={`font-bold ${
                              booking.status === 'Confirmed' ? 'text-green-600' :
                              booking.status === 'Pending' ? 'text-yellow-600' :
                              booking.status === 'Failed' ? 'text-red-600' :
                              booking.status === 'Cancelled' ? 'text-gray-600' :
                              'text-gray-600'
                            }`}>
                              {booking.status}
                            </span>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="9" className="px-4 py-24 text-center text-gray-500 font-medium bg-white">
                          {searchTerm ? 'No previous bookings match your search' : 'No previous bookings found'}
                        </td>
                      </tr>
                    )
                  )}
                </tbody>
              </table>
            </div>
            
            {/* Search results info */}
            {searchTerm && (
              <div className="px-6 py-3 bg-gray-50 border-t border-gray-200 text-sm text-gray-600">
                Found {filteredBookings.upcoming.length} upcoming and {filteredBookings.previous.length} previous booking(s) matching "{searchTerm}"
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Profile Edit Drawer */}
      {isDrawerOpen && (
        <>
          <div 
            className="fixed inset-0 bg-black/50 z-50 backdrop-blur-sm transition-opacity" 
            onClick={() => setIsDrawerOpen(false)}
          ></div>
          
          <div className={`fixed inset-y-0 right-0 w-full max-w-lg bg-white z-[60] shadow-2xl flex flex-col transition-transform duration-300 ease-in-out ${isDrawerOpen ? 'translate-x-0' : 'translate-x-full'}`}>
            
            <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center">
              <h2 className="text-sm font-bold text-gray-800 tracking-wide uppercase">Edit Profile</h2>
              <button onClick={() => setIsDrawerOpen(false)} className="text-gray-500 hover:text-black">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="flex-1 overflow-y-auto p-6">
              {titlesLoading && (
                <div className="text-center py-4">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-red-800 mx-auto"></div>
                  <p className="text-xs text-gray-500 mt-2">Loading titles...</p>
                </div>
              )}
              
              <div className="grid grid-cols-2 gap-x-4 gap-y-4">
                
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Title</label>
                  <select 
                    name="title_id"
                    value={formData.title_id || ''}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:border-red-800 focus:ring-1 focus:ring-red-800 outline-none text-sm bg-white"
                    disabled={titlesLoading}
                  >
                    <option value="">Select Title</option>
                    {titles.map((title) => (
                      <option key={title.id} value={title.id}>
                        {title.name}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-xs text-gray-600 mb-1">First Name:</label>
                  <input 
                    type="text" 
                    name="firstName"
                    value={formData.firstName || ''}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:border-red-800 focus:ring-1 focus:ring-red-800 outline-none text-sm" 
                  />
                </div>
                
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Last Name:</label>
                  <input 
                    type="text" 
                    name="lastName"
                    value={formData.lastName || ''}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:border-red-800 focus:ring-1 focus:ring-red-800 outline-none text-sm" 
                    placeholder="Enter your last name" 
                  />
                </div>
                
                <div className="hidden">
                  <label className="block text-xs text-gray-600 mb-1">Password:</label>
                  <input 
                    type="password" 
                    name="password"
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:border-red-800 focus:ring-1 focus:ring-red-800 outline-none text-sm" 
                    placeholder="Enter new password (leave blank to keep current)" 
                  />
                </div>
                
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Company Name:</label>
                  <input 
                    type="text" 
                    name="companyName"
                    value={formData.companyName || ''}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:border-red-800 focus:ring-1 focus:ring-red-800 outline-none text-sm" 
                    placeholder="Enter company name" 
                  />
                </div>
                
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Designation:</label>
                  <input 
                    type="text" 
                    name="designation"
                    value={formData.designation || ''}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:border-red-800 focus:ring-1 focus:ring-red-800 outline-none text-sm" 
                    placeholder="Enter your designation" 
                  />
                </div>
                
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Phone Number:</label>
                  <input 
                    type="tel" 
                    name="phone"
                    value={formData.phone || ''}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:border-red-800 focus:ring-1 focus:ring-red-800 outline-none text-sm" 
                    placeholder="Enter your phone number" 
                  />
                </div>
                
                <div className="col-span-2">
                  <label className="block text-xs text-gray-600 mb-1">Email:</label>
                  <input 
                    type="email" 
                    name="email"
                    value={formData.email || userEmail}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:border-red-800 focus:ring-1 focus:ring-red-800 outline-none text-sm text-blue-600" 
                    readOnly
                  />
                  <p className="text-xs text-gray-500 mt-1">Email cannot be changed</p>
                </div>
                
                <div>
                  <label className="block text-xs text-gray-600 mb-1">City:</label>
                  <input 
                    type="text" 
                    name="city"
                    value={formData.city || ''}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:border-red-800 focus:ring-1 focus:ring-red-800 outline-none text-sm" 
                    placeholder="Enter your city" 
                  />
                </div>
                
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Pincode:</label>
                  <input 
                    type="text" 
                    name="pincode"
                    value={formData.pincode || ''}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:border-red-800 focus:ring-1 focus:ring-red-800 outline-none text-sm" 
                    placeholder="Enter your pincode" 
                  />
                </div>
                
                <div className="hidden1">
                  <label className="block text-xs text-gray-600 mb-1">Date of Birth:</label>
                  <input 
                    type="date" 
                    name="dateOfBirth"
                    value={formData.dateOfBirth || ''}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:border-red-800 focus:ring-1 focus:ring-red-800 outline-none text-sm text-blue-600" 
                  />
                </div>
                
                <div className="col-span-2">
                  <label className="block text-xs text-gray-600 mb-1">Address:</label>
                  <input 
                    type="text" 
                    name="address"
                    value={formData.address || ''}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:border-red-800 focus:ring-1 focus:ring-red-800 outline-none text-sm" 
                    placeholder="Enter your address" 
                  />
                </div>
              </div>

              <div className="mt-6 flex gap-3">
                <button 
                  onClick={handleSaveProfile}
                  disabled={profileLoading || titlesLoading}
                  className={`px-8 py-2 bg-[#9b1c1c] text-white text-sm font-medium rounded hover:bg-red-800 transition-colors ${
                    (profileLoading || titlesLoading) ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                >
                  {profileLoading ? 'Saving...' : 'Save'}
                </button>
                <button 
                  onClick={() => setIsDrawerOpen(false)} 
                  className="px-8 py-2 bg-gray-100 text-gray-700 text-sm font-medium rounded hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Member;